import { Component, EventEmitter, Output, inject } from "@angular/core"
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogContent, MatDialogTitle } from "@angular/material/dialog"
import { MatIcon } from "@angular/material/icon"
import { map, BehaviorSubject } from "rxjs"
import { ToastService } from "src/app/services"

import {
    Address,
    EmptyModifyLandlordDataErrors,
    FileContent,
    isValidEmail,
    LandlordData,
    LandlordDataKeys,
    ModifyLandlordData,
    ModifyLandlordDataFactory,
    PropertyRoom,
} from "src/app/models"

import {
    AddressInputComponent,
    BooleanInputComponent,
    FileInputComponent,
    NumberInputComponent,
    TextInputComponent,
} from "src/app/components"

import { RoomsInputComponent } from "src/app/components/rooms-input/rooms-input.component"
@Component({
    selector: "valben-landlord-data-dialog",
    templateUrl: "landlord-data-dialog.component.html",
    standalone: true,
    imports: [
        MatDialogTitle,
        MatDialogContent,
        MatDialogClose,
        MatIcon,
        TextInputComponent,
        AddressInputComponent,
        FileInputComponent,
        NumberInputComponent,
        BooleanInputComponent,
        RoomsInputComponent,
    ],
})
export class LandlordDataDialogComponent {
    @Output() saveChanges = new EventEmitter<ModifyLandlordData>()

    readonly data = inject<LandlordData | null>(MAT_DIALOG_DATA)

    editMode: boolean = false
    // Once saved, the dialog holds data even though the injected snapshot is still null.
    saved: boolean = false

    landlordDataKeys = LandlordDataKeys
    modifyLandlordDataErrors: { [key: string]: boolean } = { ...EmptyModifyLandlordDataErrors }
    modifyLandlordData = () => this._modifyLandlordDataSubject.asObservable()

    private readonly _modifyLandlordDataSubject = new BehaviorSubject<ModifyLandlordData>(
        ModifyLandlordDataFactory.create()
    )

    private readonly _modifyLandlordData$ = this._modifyLandlordDataSubject.asObservable()

    private readonly _messageService = inject(ToastService)

    constructor() {
        this.resetForm()
    }

    landlordNameObservable = () => this._modifyLandlordData$.pipe(map(x => x.name))
    landlordSurnameObservable = () => this._modifyLandlordData$.pipe(map(x => x.surname))
    landlordPhoneNumberObservable = () => this._modifyLandlordData$.pipe(map(x => x.phoneNumber))
    landlordEmailObservable = () => this._modifyLandlordData$.pipe(map(x => x.email))
    landlordOibObservable = () => this._modifyLandlordData$.pipe(map(x => x.oib))
    landlordIbanObservable = () => this._modifyLandlordData$.pipe(map(x => x.iban))
    landlordSwiftObservable = () => this._modifyLandlordData$.pipe(map(x => x.swift))
    landlordCatastarKOObservable = () => this._modifyLandlordData$.pipe(map(x => x.catastarKO))
    landlordCatastarCEObservable = () => this._modifyLandlordData$.pipe(map(x => x.catastarCE))

    landlordResidenceAddressObservable = () => this._modifyLandlordData$.pipe(map(x => x.residenceAddress))
    landlordRentalPropertyAddressObservable = () => this._modifyLandlordData$.pipe(map(x => x.rentalPropertyAddress))

    landlordConsentFormObservable = () => this._modifyLandlordData$.pipe(map(x => x.consentForm))
    landlordOwnershipCertificateObservable = () => this._modifyLandlordData$.pipe(map(x => x.ownershipCertificate))

    landlordAreaOfPropertyObservable = () => this._modifyLandlordData$.pipe(map(x => x.areaOfProperty))
    landlordMonthlyRentObservable = () => this._modifyLandlordData$.pipe(map(x => x.monthlyRent))

    landlordIsApartmentOfLegalEntityObservable = () => this._modifyLandlordData$.pipe(map(x => x.isApartmentOfLegalEntity))

    landlordPropertyRoomsObservable = () => this._modifyLandlordData$.pipe(map(x => x.propertyRooms))

    get showSwift(): boolean {
        const iban = this._modifyLandlordDataSubject.value.iban?.trim()?.toUpperCase() ?? ""
        return iban !== "" && !iban.startsWith("HR")
    }

    get hasData(): boolean {
        return this.data !== null || this.saved
    }

    startEdit(): void {
        this.editMode = true
        this.resetForm()
    }

    cancelEdit(): void {
        this.editMode = false
        this.resetForm()
    }

    save(): void {
        if (!this.validate()) {
            this._messageService.add({
                severity: "error",
                summary: "Greška",
                detail: "Sva obavezna polja moraju biti ispunjena.",
                life: 10000,
            })
            return
        }

        this.saveChanges.emit(this._modifyLandlordDataSubject.value)
        this.editMode = false
        this.saved = true
    }

    private validate(): boolean {
        const data = this._modifyLandlordDataSubject.value
        const isEmptyString = (value: string): boolean => !value?.trim()
        const isFileMissing = (file: FileContent): boolean => !file?.url && !file?.base64
        const isAddressMissing = (address: Address): boolean =>
            isEmptyString(address?.street) ||
            isEmptyString(address?.streetNumber) ||
            isEmptyString(address?.city) ||
            !address?.postalCode

        const errors = { ...this.modifyLandlordDataErrors }

        errors[this.landlordDataKeys.Name] = isEmptyString(data.name)
        errors[this.landlordDataKeys.Surname] = isEmptyString(data.surname)
        errors[this.landlordDataKeys.PhoneNumber] = isEmptyString(data.phoneNumber)
        errors[this.landlordDataKeys.Email] = isEmptyString(data.email) || !isValidEmail(data.email)
        errors[this.landlordDataKeys.OIB] = isEmptyString(data.oib)
        errors[this.landlordDataKeys.Iban] = isEmptyString(data.iban)
        errors[this.landlordDataKeys.Swift] = this.showSwift && isEmptyString(data.swift)
        errors[this.landlordDataKeys.ResidenceAddress] = isAddressMissing(data.residenceAddress)
        errors[this.landlordDataKeys.RentalPropertyAddress] = isAddressMissing(data.rentalPropertyAddress)
        errors[this.landlordDataKeys.CatastarKO] = isEmptyString(data.catastarKO)
        errors[this.landlordDataKeys.CatastarCE] = isEmptyString(data.catastarCE)
        errors[this.landlordDataKeys.PropertyRooms] = !data.propertyRooms?.length
        errors[this.landlordDataKeys.AreaOfProperty] = !(data.areaOfProperty > 0)
        errors[this.landlordDataKeys.MonthlyRent] = !(data.monthlyRent > 0)
        errors[this.landlordDataKeys.ConsentForm] = isFileMissing(data.consentForm)
        errors[this.landlordDataKeys.OwnershipCertificate] = isFileMissing(data.ownershipCertificate)

        this.modifyLandlordDataErrors = errors

        return !Object.values(errors).some(Boolean)
    }

    private resetForm(): void {
        this._modifyLandlordDataSubject.next(
            this.data
                ? ModifyLandlordDataFactory.from(this.data)
                : ModifyLandlordDataFactory.create()
        )

        this.modifyLandlordDataErrors = { ...EmptyModifyLandlordDataErrors }
    }

    private patchLandlordData(patch: Partial<ModifyLandlordData>): void {
        this._modifyLandlordDataSubject.next({
            ...this._modifyLandlordDataSubject.value,
            ...patch,
        })
    }

    updateEditLandlordName = (value: string): void => {
        this.patchLandlordData({ name: value })
    }

    updateEditLandlordSurname = (value: string): void => {
        this.patchLandlordData({ surname: value })
    }

    updateEditLandlordPhoneNumber = (value: string): void => {
        this.patchLandlordData({ phoneNumber: value })
    }

    updateEditLandlordEmail = (value: string): void => {
        this.patchLandlordData({ email: value })
    }

    updateEditLandlordOIB = (value: string): void => {
        this.patchLandlordData({ oib: value })
    }

    updateEditLandlordIban = (value: string): void => {
        this.patchLandlordData({ iban: value })
    }

    updateEditLandlordSwift = (value: string): void => {
        this.patchLandlordData({ swift: value })
    }

    updateEditLandlordResidenceAddress = (value: Address): void => {
        this.patchLandlordData({ residenceAddress: value })
    }

    updateEditLandlordPropertyRooms = (value: PropertyRoom[]): void => {
        this.patchLandlordData({ propertyRooms: value })
    }

    updateEditLandlordAreaOfProperty = (value: number): void => {
        this.patchLandlordData({ areaOfProperty: value })
    }

    updateEditLandlordIsApartmentOfLegalEntity = (value: boolean): void => {
        this.patchLandlordData({ isApartmentOfLegalEntity: value })
    }

    updateEditLandlordRentalPropertyAddress = (value: Address): void => {
        this.patchLandlordData({ rentalPropertyAddress: value })
    }

    updateEditLandlordMonthlyRent = (value: number): void => {
        this.patchLandlordData({ monthlyRent: value })
    }

    updateEditLandlordConsentForm = (value: FileContent): void => {
        this.patchLandlordData({ consentForm: value })
    }

    updateEditLandlordOwnershipCertificate = (value: FileContent): void => {
        this.patchLandlordData({ ownershipCertificate: value })
    }

    updateEditLandlordCatastarKO = (value: string): void => {
        this.patchLandlordData({ catastarKO: value })
    }

    updateEditLandlordCatastarCE = (value: string): void => {
        this.patchLandlordData({ catastarCE: value })
    }
}
